/**
 * Derives the OCI source-record review model from document and analysis data.
 *
 * Connectors own business vocabulary such as Award, Subaward, and Negotiation.
 * This model consumes that shared provenance so OCI can group any supported
 * source record without coupling the review application to Kuali modules.
 */
import type { DocumentListItem } from '@/subsystems/documents'


export type OciReviewStatus = 'not-run' | 'clear' | 'flagged' | 'stale' | 'error'
export type OciFindingsState = 'loading' | 'ready' | 'error'
export type OciSortKey = 'record' | 'document' | 'findings' | 'updated'
export type OciRecordSortKey = 'record' | 'findings' | 'updated'
export type SortDirection = 'asc' | 'desc'

export type OciDocument = Pick<
  DocumentListItem,
  | 'documentId'
  | 'name'
  | 'latestFilename'
  | 'latestVersion'
  | 'latestVersionAt'
  | 'uploadedBy'
  | 'sourceGroupKey'
  | 'sourceGroupName'
  | 'sourceDisplayFields'
  | 'recordType'
  | 'groupId'
> & { feedId?: string | null }

export type OciFeedActivity = 'syncing' | 'analyzing'

export interface OciAnalysisStatus {
  version?: number
  count?: number
  type?: string
  updatedAt?: string
}

export interface OciDashboardRow {
  documentId: string
  documentName: string
  version: number
  recordName: string | null
  recordType: string | null
  categories: string[]
  findingCount: number
  reportedFindingCount: number
  findingsState: OciFindingsState
  status: OciReviewStatus
  updatedAt: string | null
}

export interface OciDocumentReview {
  document: OciDocument
  analysis: OciAnalysisStatus
}

export interface OciRecordSummary {
  key: string
  feedId: string | null
  recordName: string | null
  sourceDisplayFields: DocumentListItem['sourceDisplayFields']
  recordTypes: OciRecordTypeGroup[]
  attachmentCount: number
  analyzedAttachmentCount: number
  findingCount: number
  status: OciReviewStatus
  syncedBy: string | null
  syncedAt: string
  updatedAt: string | null
  searchTerms: string[]
}

export interface OciRecordGroup extends OciRecordSummary {
  reviews: OciDocumentReview[]
}

export interface OciRecordTypeGroup {
  key: string
  recordType: string
  attachmentCount: number
  findingCount: number
}

const OCI_CATEGORIES = new Set([
  'Biased Ground Rules',
  'Unequal Access',
  'Impaired Objectivity',
  'Mitigation',
])

export const OCI_STATUS_LABELS: Record<OciReviewStatus, string> = {
  'not-run': 'Not analyzed',
  clear: 'Clear',
  flagged: 'OCI findings',
  stale: 'Needs rerun',
  error: 'Run failed',
}

function stringField(
  item: Record<string, unknown>,
  camel: string,
  snake: string,
): string | null {
  const value = item[camel] ?? item[snake]
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

export function findingsFromPayload(payload?: Record<string, unknown>): Record<string, unknown>[] {
  const findings = payload?.findings
  return Array.isArray(findings)
    ? findings.filter((item): item is Record<string, unknown> => (
      typeof item === 'object' && item !== null
    ))
    : []
}

export function ociFindingsFromPayload(payload?: Record<string, unknown>): Record<string, unknown>[] {
  return findingsFromPayload(payload).filter((item) => {
    const category = stringField(item, 'clauseType', 'clause_type')
    return category !== null && OCI_CATEGORIES.has(category)
  })
}

export function buildOciDashboardRow(
  document: OciDocument,
  analysis: OciAnalysisStatus,
  payload?: Record<string, unknown>,
  findingsState: OciFindingsState = 'ready',
): OciDashboardRow {
  const findings = findingsFromPayload(payload)
  // Domain boundary: generic clause-analysis output is not OCI evidence. Only
  // the categories promised by the migrated OCI prompt can drive this dashboard.
  const ociFindings = ociFindingsFromPayload(payload)
  const categories = [...new Set(
    ociFindings
      .map(item => stringField(item, 'clauseType', 'clause_type'))
      .filter((value): value is string => value !== null),
  )].sort((left, right) => left.localeCompare(right))
  const findingCount = ociFindings.length
  const status: OciReviewStatus = analysis.type === 'error'
    ? 'error'
    : analysis.version === undefined
      ? 'not-run'
      : analysis.version !== document.latestVersion
        ? 'stale'
        : findingCount > 0
          ? 'flagged'
          : findings.length > 0
            ? 'stale'
            : 'clear'

  return {
    documentId: document.documentId,
    documentName: document.latestFilename || document.name,
    version: document.latestVersion,
    recordName: document.sourceGroupName,
    recordType: document.recordType,
    categories,
    findingCount,
    reportedFindingCount: (
      analysis.version === document.latestVersion && analysis.type !== 'error'
        ? analysis.count ?? 0
        : 0
    ),
    findingsState,
    status,
    updatedAt: analysis.updatedAt ?? null,
  }
}

export function hasCurrentSuccessfulAnalysis(review: OciDocumentReview): boolean {
  // cm-coding: A zero-finding OCI result is meaningful only for the attachment
  // version the reviewer can currently open. Treat older results and failed
  // runs as unfinished so record summaries, progress, and CSV exports cannot
  // turn missing review evidence into a false "None reported" conclusion.
  return review.analysis.version === review.document.latestVersion
    && review.analysis.type !== 'error'
}

function currentFindingCount(review: OciDocumentReview): number {
  return hasCurrentSuccessfulAnalysis(review) ? (review.analysis.count ?? 0) : 0
}

export function countOciDocumentsWithFindings(reviews: OciDocumentReview[]): number {
  // cm-coding: The dashboard label reports documents with findings, not awards
  // with findings. Count current successful document analyses directly so an
  // award with several attachments cannot be reported as a single reviewed
  // document.
  return reviews.filter(review => currentFindingCount(review) > 0).length
}

function mergeSourceDisplayFields(
  existing: DocumentListItem['sourceDisplayFields'],
  incoming: DocumentListItem['sourceDisplayFields'],
) {
  const merged = [...existing]
  for (const field of incoming) {
    if (!merged.some(candidate => candidate.key === field.key)) merged.push(field)
  }
  return merged
}

export function filterOciRows(rows: OciDashboardRow[], query: string): OciDashboardRow[] {
  const term = query.trim().toLocaleLowerCase()
  if (!term) return rows
  return rows.filter(row => [
    row.recordName,
    row.recordType,
    row.documentId,
    row.documentName,
    OCI_STATUS_LABELS[row.status],
    ...row.categories,
  ].some(value => value?.toLocaleLowerCase().includes(term)))
}

export function sortOciRows(
  rows: OciDashboardRow[],
  key: OciSortKey,
  direction: SortDirection,
): OciDashboardRow[] {
  const multiplier = direction === 'asc' ? 1 : -1
  return [...rows].sort((left, right) => {
    const comparison = key === 'findings'
      ? left.reportedFindingCount - right.reportedFindingCount
      : key === 'record'
        ? (left.recordName ?? '').localeCompare(right.recordName ?? '')
        : key === 'updated'
          ? (Date.parse(left.updatedAt ?? '') || 0) - (Date.parse(right.updatedAt ?? '') || 0)
          : left.documentName.localeCompare(right.documentName)
    return comparison === 0
      ? left.documentId.localeCompare(right.documentId) * multiplier
      : comparison * multiplier
  })
}

export function buildOciRecordGroups(reviews: OciDocumentReview[]): OciRecordGroup[] {
  const grouped = new Map<string, OciDocumentReview[]>()
  for (const review of reviews) {
    const { sourceGroupKey, sourceGroupName } = review.document
    if ((sourceGroupKey === null) !== (sourceGroupName === null)) {
      throw new Error(`Document ${review.document.documentId} has incomplete source grouping`)
    }
    // cm-coding: Connector group identity, display name, and record type are
    // separate on purpose. Grouping by a human label would merge unrelated
    // records after a rename; grouping missing provenance together would invent
    // a source record and misstate the review totals.
    const key = sourceGroupKey ?? `unlinked:${review.document.documentId}`
    const group = grouped.get(key) ?? []
    group.push(review)
    grouped.set(key, group)
  }

  return [...grouped.entries()].map(([key, groupReviews]) => {
    const recordNames = new Set(groupReviews.map(review => review.document.sourceGroupName))
    if (recordNames.size !== 1) {
      throw new Error(`Source record ${key} has conflicting provenance`)
    }
    const feedIds = new Set(groupReviews.map(review => review.document.feedId ?? null))
    if (feedIds.size !== 1) {
      throw new Error(`Source record ${key} belongs to multiple connector feeds`)
    }
    // cm-coding: Attachments for one OCI award can carry different subsets of
    // optional provider context. Merge display values by their stable field key
    // so reviewers receive all available context, while the strict group checks
    // above still reject conflicting award identity or provenance.
    const sourceDisplayFields = groupReviews.reduce(
      (fields, review) => mergeSourceDisplayFields(
        fields,
        review.document.sourceDisplayFields,
      ),
      [] as DocumentListItem['sourceDisplayFields'],
    )
    const reviewsByType = new Map<string, OciDocumentReview[]>()
    for (const review of groupReviews) {
      const recordType = review.document.recordType ?? 'Document'
      const typeReviews = reviewsByType.get(recordType) ?? []
      typeReviews.push(review)
      reviewsByType.set(recordType, typeReviews)
    }
    const recordTypes = [...reviewsByType.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([recordType, reviews]) => ({
        key: `${key}:${recordType}`,
        recordType,
        reviews,
        attachmentCount: reviews.length,
        findingCount: reviews.reduce(
          (sum, review) => sum + currentFindingCount(review),
          0,
        ),
      }))

    // cm-coding: Dashboard startup must stay independent of attachment volume.
    // The bulk analysis count identifies records worth reviewing without one
    // payload request per attachment; expansion validates the exact OCI
    // categories before presenting document-level findings to the reviewer.
    const findingCount = groupReviews.reduce(
      (sum, review) => sum + currentFindingCount(review),
      0,
    )
    const analyzedAttachmentCount = groupReviews.filter(
      hasCurrentSuccessfulAnalysis,
    ).length
    const dates = groupReviews
      .map(review => review.analysis.updatedAt)
      .filter((value): value is string => Boolean(value))
    // cm-coding: A source-record summary has one sync audit pair even when the
    // record has several attachments. Select both values from the newest
    // attachment so the dashboard never combines one user's identity with a
    // different document's sync timestamp.
    const latestSync = groupReviews.reduce((latest, review) => (
      Date.parse(review.document.latestVersionAt)
        > Date.parse(latest.document.latestVersionAt)
        ? review
        : latest
    ))
    // cm-coding: Sync status describes whether the complete source record is
    // reviewable, while findingCount may contain valid evidence from only some
    // attachments. Incomplete evidence must win over a partial finding count so
    // one successful attachment cannot hide a failed, stale, or missing run.
    const status: OciReviewStatus = groupReviews.some(review => (
      review.analysis.version === review.document.latestVersion
      && review.analysis.type === 'error'
    ))
      ? 'error'
      : groupReviews.some(review => (
        review.analysis.version !== undefined
        && review.analysis.version !== review.document.latestVersion
      ))
        ? 'stale'
        : analyzedAttachmentCount < groupReviews.length
          ? 'not-run'
          : findingCount > 0
            ? 'flagged'
            : 'clear'

    return {
      key,
      feedId: groupReviews[0]?.document.feedId ?? null,
      recordName: groupReviews[0]?.document.sourceGroupName ?? null,
      sourceDisplayFields,
      recordTypes,
      reviews: groupReviews,
      attachmentCount: groupReviews.length,
      analyzedAttachmentCount,
      findingCount,
      status,
      syncedBy: latestSync.document.uploadedBy,
      syncedAt: latestSync.document.latestVersionAt,
      updatedAt: dates.sort().at(-1) ?? null,
      searchTerms: [
        ...groupReviews.flatMap(review => [
          review.document.documentId,
          review.document.latestFilename,
          review.document.name,
        ]),
        ...sourceDisplayFields.flatMap(field => [field.label, field.value]),
        ...recordTypes.map(recordType => recordType.recordType),
      ],
    }
  })
}

export function filterOciRecordGroups(
  groups: OciRecordSummary[],
  query: string,
  findingsOnly: boolean,
): OciRecordSummary[] {
  const term = query.trim().toLocaleLowerCase()
  return groups.filter(group => {
    // cm-coding: The default view is the reviewer's attention queue. A failed
    // sync has no trustworthy zero-finding result, so keep it visible beside
    // reported findings instead of requiring reviewers to discover it in All.
    if (
      findingsOnly
      && group.findingCount === 0
      && group.status !== 'error'
    ) return false
    if (!term) return true
    return [
      group.recordName,
      OCI_STATUS_LABELS[group.status],
      ...group.searchTerms,
    ].some(value => value?.toLocaleLowerCase().includes(term))
  })
}

export function sortOciRecordGroups(
  groups: OciRecordSummary[],
  key: OciRecordSortKey,
  direction: SortDirection,
): OciRecordSummary[] {
  const multiplier = direction === 'asc' ? 1 : -1
  return [...groups].sort((left, right) => {
    const comparison = key === 'findings'
      ? left.findingCount - right.findingCount
      : key === 'updated'
        ? (Date.parse(left.updatedAt ?? '') || 0) - (Date.parse(right.updatedAt ?? '') || 0)
        : (left.recordName ?? '').localeCompare(right.recordName ?? '')
    return comparison === 0
      ? left.key.localeCompare(right.key) * multiplier
      : comparison * multiplier
  })
}
